import React from 'react';
import { Line } from 'react-chartjs-2';

/**
 * LineGraph - Premium Line Chart Component
 *
 * Uses design system colors for a cohesive look.
 * Features smooth animations and refined styling.
 */
const LineGraph = (props) => {
  let { title, title1, title2, title3, title4, x, y, y1, y2, y3, y4, width, height } = props;

  // Design system colors
  const colors = {
    primary: {
      main: '#6366F1',
      light: 'rgba(99, 102, 241, 0.1)'
    },
    secondary: {
      main: '#0EA5E9',
      light: 'rgba(14, 165, 233, 0.1)'
    },
    accent: {
      main: '#10B981',
      light: 'rgba(16, 185, 129, 0.1)'
    },
    warning: {
      main: '#F59E0B',
      light: 'rgba(245, 158, 11, 0.1)'
    },
    danger: {
      main: '#EF4444',
      light: 'rgba(239, 68, 68, 0.1)'
    }
  };

  const datasets = [
    {
      label: title || 'Cost',
      fill: false,
      lineTension: 0.4,
      borderColor: colors.primary.main,
      borderWidth: 2,
      borderCapStyle: 'round',
      borderJoinStyle: 'round',
      pointRadius: 0,
      pointHitRadius: 10,
      pointHoverRadius: 5,
      pointHoverBackgroundColor: colors.primary.main,
      pointHoverBorderColor: '#FFFFFF',
      pointHoverBorderWidth: 2,
      data: y
    },
    {
      label: title1 || 'Minutes',
      fill: false,
      lineTension: 0.4,
      borderColor: colors.secondary.main,
      borderWidth: 2,
      borderCapStyle: 'round',
      borderJoinStyle: 'round',
      pointRadius: 0,
      pointHitRadius: 10,
      pointHoverRadius: 5,
      pointHoverBackgroundColor: colors.secondary.main,
      pointHoverBorderColor: '#FFFFFF',
      pointHoverBorderWidth: 2,
      data: y1
    }
  ];

  if (y2) {
    datasets.push({
      label: title2 || 'Total Dialed Calls',
      fill: false,
      lineTension: 0.4,
      borderColor: colors.accent.main,
      borderWidth: 2,
      borderCapStyle: 'round',
      borderJoinStyle: 'round',
      pointRadius: 0,
      pointHitRadius: 10,
      pointHoverRadius: 5,
      pointHoverBackgroundColor: colors.accent.main,
      pointHoverBorderColor: '#FFFFFF',
      pointHoverBorderWidth: 2,
      data: y2
    });
  }

  if (y3) {
    datasets.push({
      label: title3 || 'ASR (%)',
      fill: false,
      lineTension: 0.4,
      borderColor: colors.warning.main,
      borderWidth: 2,
      borderCapStyle: 'round',
      borderJoinStyle: 'round',
      pointRadius: 0,
      pointHitRadius: 10,
      pointHoverRadius: 5,
      pointHoverBackgroundColor: colors.warning.main,
      pointHoverBorderColor: '#FFFFFF',
      pointHoverBorderWidth: 2,
      data: y3
    });
  }

  if (y4) {
    datasets.push({
      label: title4 || 'ACD (s)',
      fill: false,
      lineTension: 0.4,
      borderColor: colors.danger.main,
      borderWidth: 2,
      borderCapStyle: 'round',
      borderJoinStyle: 'round',
      pointRadius: 0,
      pointHitRadius: 10,
      pointHoverRadius: 5,
      pointHoverBackgroundColor: colors.danger.main,
      pointHoverBorderColor: '#FFFFFF',
      pointHoverBorderWidth: 2,
      data: y4
    });
  }

  const dataSets = {
    labels: x,
    datasets: datasets
  };

  var options = {
    maintainAspectRatio: false,
    responsive: true,
    animation: {
      duration: 800,
      easing: 'easeOutQuart'
    },
    scales: {
      yAxes: [
        {
          stacked: false,
          gridLines: {
            color: 'rgba(226, 232, 240, 0.6)',
            drawBorder: false,
            zeroLineColor: 'rgba(226, 232, 240, 0.8)'
          },
          ticks: {
            beginAtZero: true,
            fontSize: 11,
            fontColor: '#64748B',
            fontFamily: "'Inter', sans-serif",
            padding: 10,
            callback: function(value) {
              if (value >= 1000000) return (value / 1000000).toFixed(1).replace(/\.0$/, '') + 'M';
              if (value >= 1000) return (value / 1000).toFixed(1).replace(/\.0$/, '') + 'k';
              return value;
            }
          }
        }
      ],
      xAxes: [
        {
          stacked: false,
          gridLines: {
            display: false,
            drawBorder: false
          },
          ticks: {
            fontSize: 11,
            fontColor: '#64748B',
            fontFamily: "'Inter', sans-serif",
            padding: 5,
            maxTicksLimit: 24,
            autoSkip: true
          }
        }
      ]
    },
    legend: {
      position: 'bottom',
      display: true,
      labels: {
        boxWidth: 12,
        fontColor: '#475569',
        fontFamily: "'Inter', sans-serif",
        fontSize: 12,
        fontStyle: '500',
        padding: 20,
        usePointStyle: true
      }
    },
    tooltips: {
      enabled: true,
      mode: 'index',
      intersect: false,
      backgroundColor: 'rgba(15, 23, 42, 0.9)',
      titleFontFamily: "'Inter', sans-serif",
      titleFontSize: 12,
      titleFontStyle: '600',
      titleFontColor: '#FFFFFF',
      bodyFontFamily: "'Inter', sans-serif",
      bodyFontSize: 12,
      bodyFontColor: '#E2E8F0',
      borderColor: 'rgba(255, 255, 255, 0.1)',
      borderWidth: 1,
      cornerRadius: 8,
      displayColors: true,
      xPadding: 12,
      yPadding: 10,
      caretSize: 6,
      caretPadding: 10
    },
    hover: {
      mode: 'index',
      intersect: false
    },
    elements: {
      line: {
        tension: 0.4
      },
      point: {
        radius: 0,
        hoverRadius: 6
      }
    }
  };

  return (
    <div style={{ position: 'relative', height: height || 300 }}>
      <Line width={width} height={height} data={dataSets} options={options} />
    </div>
  );
};

export default LineGraph;
